import axios, { AxiosRequestConfig } from "axios";
import { inflate } from "pako";
import { BaseDataModel } from "./types/dataModels";

export const USER_NOT_FOUND = "User not found";
export const USER_NOT_LINKED = "User must be linked to at least one company";
export const NEW_USER_TOKEN = "new_user";

export const REQUEST_CONFIG_NOAUTH: AxiosRequestConfig<any> = {
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json",
	},
};

export function getLoginToken() {
	return localStorage.getItem("rogo_id_token");
}
export function setLoginToken(token: string) {
	return localStorage.setItem("rogo_id_token", token);
}

interface AxiosConfigOptions {
	isFileUpload?: boolean;
	isCompressed?: boolean;
}

/**
 * @param seconds - The timeout duration in seconds for the request. Defaults to 0 (no timeout).
 */
export function StandardAxiosConfig(
	seconds: number = 0,
	{ isFileUpload, isCompressed }: AxiosConfigOptions = {},
): AxiosRequestConfig<any> {
	const config: AxiosRequestConfig<any> = {
		headers: { Authorization: `Bearer ${getLoginToken()}` },
		timeout: seconds * 1000,
	};
	if (isFileUpload) {
		(config.headers as any)["Content-Type"] = "multipart/form-data";
	}
	if (isCompressed) {
		config.responseType = "blob";
	}
	return config;
}

export async function getCompressed(url: string, filter: Filter<any> = {} as Filter<any>): Promise<any[]> {
	const response = await axios.post(url, filter, StandardAxiosConfig(0, { isCompressed: true }));
	const decompressedData = inflate(await response.data.arrayBuffer(), { to: "string" });
	const records = JSON.parse(decompressedData);
	return records;
}

export interface ProagricaAuth {
	access_token: string;
	expires_in: number;
	id_token: string;
	refresh_token: string;
	scope: string;
	token_type: string;
}
export interface AuthUser {
	id: string;
	name: string;
	token: string;
	username: string;
	email: string;
	full_name: string;
	disabled: boolean;
	companyIds: string[];
	proagrica_auth?: ProagricaAuth;
	isSuperUser: boolean;
	isDev: boolean;
}

/**
 * Operator filter for numeric comparisons.
 * Allows specifying comparison operators for number fields.
 *
 * Example: { operator: ">", value: 25 }
 */
export interface OperatorFilter {
	operator: "=" | "!=" | ">" | "<" | ">=" | "<=";
	value: number;
}

/**
 * For querying data.
 * Supports nested filters.
 * Fields can be either a dictionary of field_name -> value pairs,
 * or an array containing Filter objects and/or simple field dictionaries.
 *
 * For number fields, you can use either:
 * - Simple value: { age: 25 } - defaults to equals
 * - Operator filter: { age: { operator: ">", value: 25 } } - uses specified operator
 *
 * Examples:
 * - Simple equality: { fields: { age: 25, name: "John" } }
 * - Greater than: { fields: { age: { operator: ">", value: 25 } } }
 * - Range (using nested filters): { type: "AND", fields: [{ age: { operator: ">=", value: 18 } }, { age: { operator: "<=", value: 65 } }] }
 */
export interface Filter<T> {
	type?: "AND" | "OR";
	fields: Partial<T> | FilterFields<T> | (Filter<T> | Partial<T> | FilterFields<T>)[];
}

/**
 * Helper type that allows fields to accept either their normal value or an OperatorFilter.
 * This enables both simple values and operator-based filtering for numeric fields.
 */
export type FilterFields<T> = {
	[K in keyof T]?: T[K] | OperatorFilter;
};

// Short duration JWT token (5-10 min)
export function getJwtToken() {
	return localStorage.getItem("jwt");
}

export function setJwtToken(token: string) {
	localStorage.setItem("jwt", token);
}

// Longer duration refresh token (30-60 min)
export function getRefreshToken() {
	return localStorage.getItem("refreshToken");
}

export function setRefreshToken(token: string) {
	localStorage.setItem("refreshToken", token);
}

const SPRING = "A-Spr";
const SUMMER = "B-Sum";
const FALL = "C-Fa";
const WINTER = "D-Win";

/**
 * @returns The season code (e.g. "24A-Spr")
 */
export function getSeasonCode(date: Date): string {
	const year = date.getFullYear() % 100; // Get last two digits of the year
	const month = date.getMonth(); // Get month (0-11)

	let season: string;
	if (month >= 2 && month <= 4) {
		season = SPRING; // March - May
	} else if (month >= 5 && month <= 7) {
		season = SUMMER; // June - August
	} else if (month >= 8 && month <= 10) {
		season = FALL; // September - November
	} else {
		season = WINTER; // December - February
	}

	return `${year}${season}`;
}

/**
 * @param seasonCode The season code (e.g. "24A-Spr")
 * @returns The previous season code (e.g. "23D-Win")
 */
export function getPreviousSeasonCode(date: Date): string {
	const seasonCode = getSeasonCode(date);
	const year = parseInt(seasonCode.slice(0, 2), 10);
	const season = seasonCode.slice(2);

	let previousSeason: string;
	let previousYear = year;

	switch (season) {
		case SPRING:
			previousSeason = WINTER;
			previousYear -= 1;
			break;
		case SUMMER:
			previousSeason = SPRING;
			break;
		case FALL:
			previousSeason = SUMMER;
			break;
		case WINTER:
			previousSeason = FALL;
			break;
		default:
			throw new Error("Invalid season code");
	}

	return `${previousYear < 10 ? "0" : ""}${previousYear}${previousSeason}`;
}

export function getBaseDataURL(router: string) {
	return `${import.meta.env.VITE_ROGO_API_URL}/${router}`;
}

export function parseDates<U extends BaseDataModel>(records: U[]) {
	records.forEach((record) => {
		for (const key in record) {
			if (
				record[key] &&
				typeof record[key] === "string" &&
				!isNaN(Date.parse(record[key])) &&
				(key as string).includes("date")
			) {
				record[key] = new Date(record[key]) as any;
			}
		}
	});
}
